import React, { useState } from "react";
import { Box, Button, MenuItem, Stack, TextField, CircularProgress } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import {
    ACTIVITY_STATUSES,
    DEFAULT_STATUS,
    normalizeStatus,
    statusLabel,
} from "./activityConstants";

// Add / edit one activity, inline in the panel rather than in a second drawer —
// a drawer over a drawer to type four fields is the dense control the design rule
// rules out.
//
// One column, in the order a change manager thinks: what is it, what's the
// detail, where has it got to, when, what's the proof. An activity is tracked
// by its name alone — there is no category to file it under.
//
// `impacts` turns on a field ABOVE the rest: which impact this belongs
// to. The panel omits it — there the impact is the drawer you are standing in —
// and the dashboard passes it, because there the answer is not yet known. It
// rides back through `onSave(payload, impactId)` rather than in the payload: the
// activity routes are scoped by URL, so the impact is never a body field.
export default function ActivityForm({
    activity,
    impacts,
    defaultImpactId,
    saving,
    onSave,
    onCancel,
}) {
    const intl = useIntl();
    const isEdit = Boolean(activity?.id);
    const needsImpact = Array.isArray(impacts) && impacts.length > 0;

    const [impactId, setImpactId] = useState(
        activity?.impact ?? defaultImpactId ?? impacts?.[0]?.id ?? ""
    );

    const [title, setTitle] = useState(activity?.title || "");
    const [description, setDescription] = useState(activity?.description || "");
    const [status, setStatus] = useState(normalizeStatus(activity?.status) || DEFAULT_STATUS);
    const [plannedDate, setPlannedDate] = useState(activity?.planned_date || "");
    // Only offered when editing: a brand-new activity that is already done and
    // dated is rare enough that showing the field to everyone costs more than it
    // saves. The two dates stay UNCOUPLED from status either way — the BE does
    // not enforce it and neither does this.
    const [completedDate, setCompletedDate] = useState(activity?.completed_date || "");
    const [evidence, setEvidence] = useState(activity?.evidence || "");

    const [titleError, setTitleError] = useState(false);

    const submit = () => {
        const noTitle = !title.trim();
        setTitleError(noTitle);
        if (noTitle) return;
        onSave(
            {
                title: title.trim(),
                description: description.trim(),
                status,
                // "" would be a 400 on a DateField; null is how a date is cleared.
                planned_date: plannedDate || null,
                ...(isEdit ? { completed_date: completedDate || null } : {}),
                evidence: evidence.trim(),
            },
            needsImpact ? Number(impactId) : undefined
        );
    };

    return (
        <Stack spacing={2}>
            {needsImpact && (
                <TextField
                    select
                    fullWidth
                    size="small"
                    label={intl.formatMessage({
                        id: "activity.field_impact",
                        defaultMessage: "Impact",
                    })}
                    value={impactId === "" ? "" : String(impactId)}
                    onChange={(e) => setImpactId(e.target.value)}
                >
                    {impacts.map((impact) => (
                        <MenuItem key={impact.id} value={String(impact.id)}>
                            {impact.what_is_changing}
                        </MenuItem>
                    ))}
                </TextField>
            )}

            <TextField
                fullWidth
                required
                size="small"
                autoFocus
                label={intl.formatMessage({ id: "activity.field_title", defaultMessage: "Activity" })}
                value={title}
                error={titleError}
                helperText={
                    titleError
                        ? intl.formatMessage({
                              id: "activity.title_required",
                              defaultMessage: "Give the activity a name",
                          })
                        : " "
                }
                onChange={(e) => {
                    setTitle(e.target.value);
                    if (titleError) setTitleError(false);
                }}
            />

            <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                label={intl.formatMessage({
                    id: "activity.field_description",
                    defaultMessage: "Details",
                })}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                    select
                    fullWidth
                    size="small"
                    label={intl.formatMessage({ id: "activity.field_status", defaultMessage: "Status" })}
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                >
                    {ACTIVITY_STATUSES.map((value) => (
                        <MenuItem key={value} value={value}>
                            {statusLabel(intl, value)}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    fullWidth
                    size="small"
                    type="date"
                    // A date input renders its own placeholder, so the label has to
                    // be shrunk or it sits on top of "dd/mm/yyyy".
                    InputLabelProps={{ shrink: true }}
                    label={intl.formatMessage({
                        id: "activity.field_planned_date",
                        defaultMessage: "Planned date",
                    })}
                    value={plannedDate}
                    onChange={(e) => setPlannedDate(e.target.value)}
                />
            </Stack>

            {isEdit && (
                <TextField
                    fullWidth
                    size="small"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    label={intl.formatMessage({
                        id: "activity.field_completed_date",
                        defaultMessage: "Completed date",
                    })}
                    value={completedDate}
                    onChange={(e) => setCompletedDate(e.target.value)}
                />
            )}

            <TextField
                fullWidth
                size="small"
                label={intl.formatMessage({
                    id: "activity.field_evidence",
                    defaultMessage: "Evidence — a link or a note",
                })}
                value={evidence}
                onChange={(e) => setEvidence(e.target.value)}
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <Button size="small" onClick={onCancel} disabled={saving} sx={{ color: "text.secondary" }}>
                    <FormattedMessage id="general.cancel" defaultMessage="Cancel" />
                </Button>
                <Button
                    size="small"
                    variant="contained"
                    onClick={submit}
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
                    sx={{ backgroundColor: "#00438E", "&:hover": { backgroundColor: "#003A75" } }}
                >
                    <FormattedMessage id="general.save" defaultMessage="Save" />
                </Button>
            </Box>
        </Stack>
    );
}
